import {
  type AllSseEvents,
  type SseStreamEvent,
  SseSystemEvent,
} from '@sparrow-card/sparrow-card-constants';
import { SseAbortedError, SseError } from 'utils/sse/SseError';

const parseSseData = (data: string): SseStreamEvent => {
  return JSON.parse(data) as SseStreamEvent;
};

export type CreateWaitForSseEventOptions = {
  eventSource?: EventSource;
  closeOnEvents?: AllSseEvents[];
  signal?: AbortSignal;
};

export const createWaitForSseEvent = (
  url: string,
  finalEvents: AllSseEvents[],
  options?: CreateWaitForSseEventOptions,
): Promise<SseStreamEvent> => {
  const ownsEventSource = !options?.eventSource;
  const eventSource = options?.eventSource ?? new EventSource(url);
  const closeOnEvents = options?.closeOnEvents ?? finalEvents;
  const signal = options?.signal;

  return new Promise((resolve, reject) => {
    let settled = false;
    let errorDeferTimeout: ReturnType<typeof setTimeout> | undefined;
    const listeners: Array<{ type: string; handler: (event: MessageEvent) => void }> = [];

    const removeListeners = () => {
      listeners.forEach(({ type, handler }) => {
        eventSource.removeEventListener(type, handler);
      });
      listeners.length = 0;
    };

    let settle: (handler: () => void, shouldClose?: boolean) => void = () => undefined;

    const handleAbort = () => {
      settle(() => {
        reject(new SseAbortedError());
      }, ownsEventSource);
    };

    const cleanup = (shouldClose: boolean) => {
      if (errorDeferTimeout) {
        clearTimeout(errorDeferTimeout);
        errorDeferTimeout = undefined;
      }

      signal?.removeEventListener('abort', handleAbort);
      removeListeners();

      if (shouldClose) {
        eventSource.close();
      }
    };

    settle = (handler, shouldClose = true) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup(shouldClose);
      handler();
    };

    const handleFinalEvent = (event: MessageEvent) => {
      let parsed: SseStreamEvent;

      try {
        parsed = parseSseData(event.data);
      } catch {
        settle(() => {
          reject(new SseError('failed to parse event payload'));
        }, ownsEventSource);

        return;
      }

      const shouldClose = ownsEventSource && closeOnEvents.includes(parsed.event as AllSseEvents);

      settle(() => {
        resolve(parsed);
      }, shouldClose);
    };

    const addListener = (type: string, handler: (event: MessageEvent) => void) => {
      eventSource.addEventListener(type, handler);
      listeners.push({ type, handler });
    };

    if (signal?.aborted) {
      handleAbort();

      return;
    }

    signal?.addEventListener('abort', handleAbort);

    finalEvents.forEach((eventType) => {
      addListener(eventType, handleFinalEvent);
    });

    addListener(SseSystemEvent.Timeout, () => {
      settle(() => {
        reject(new SseError('connection timed out'));
      }, ownsEventSource);
    });

    const handleError = () => {
      if (settled) {
        return;
      }

      // EventSource fires `error` when the server closes after the final event.
      // Defer rejection so typed event listeners can settle first.
      errorDeferTimeout = setTimeout(() => {
        errorDeferTimeout = undefined;

        if (settled) {
          return;
        }

        if (eventSource.readyState === EventSource.CONNECTING) {
          return;
        }

        settle(() => {
          reject(new SseError('connection failed'));
        }, ownsEventSource);
      }, 0);
    };

    addListener('error', handleError);
  });
};
